import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import styles from './Profiel.module.css';

export default function Profiel() {
  const navigate                            = useNavigate();
  const { herlaadGebruiker, heeftPermissie } = useAuth();
  const [profiel, setProfiel]               = useState(null);
  const [afdelingen, setAfdelingen]         = useState([]);
  const [functies, setFuncties]             = useState([]);
  const [laden, setLaden]                   = useState(true);

  // Wachtwoord form
  const [huidigWw, setHuidigWw]             = useState('');
  const [nieuwWw, setNieuwWw]               = useState('');
  const [bevestigWw, setBevestigWw]         = useState('');
  const [wwLaden, setWwLaden]               = useState(false);

  // Beller standaard form
  const [bellerNaam, setBellerNaam]         = useState('');
  const [bellerFunctie, setBellerFunctie]   = useState('');
  const [bellerAfdelingId, setBellerAfdId]  = useState('');
  const [bellerActief, setBellerActief]     = useState(false);
  const [bellerLaden, setBellerLaden]       = useState(false);

  useEffect(() => {
    axios.get('/api/profiel')
      .then(res => {
        const p = res.data.profiel;
        setProfiel(p);
        setAfdelingen(res.data.afdelingen || []);
        setFuncties(res.data.functies || []);
        setBellerNaam(p.beller_naam || '');
        setBellerFunctie(p.beller_functie || '');
        setBellerAfdId(p.beller_afdeling_id || '');
        setBellerActief(!!p.beller_actief);
      })
      .catch(() => toast.error('Profiel laden mislukt.'))
      .finally(() => setLaden(false));
  }, []);

  async function handleWachtwoord(e) {
    e.preventDefault();
    if (nieuwWw !== bevestigWw) return toast.error('Wachtwoorden komen niet overeen.');
    if (nieuwWw.length < 8) return toast.error('Nieuw wachtwoord moet minimaal 8 tekens bevatten.');
    setWwLaden(true);
    try {
      await axios.put('/api/profiel/wachtwoord', {
        huidig_wachtwoord: huidigWw,
        nieuw_wachtwoord:  nieuwWw,
      });
      toast.success('Wachtwoord succesvol gewijzigd.');
      setHuidigWw(''); setNieuwWw(''); setBevestigWw('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Wijzigen mislukt.');
    } finally {
      setWwLaden(false);
    }
  }

  async function handleBeller(e) {
    e.preventDefault();
    setBellerLaden(true);
    try {
      await axios.put('/api/profiel/beller', {
        beller_naam:        bellerNaam,
        beller_functie:     bellerFunctie,
        beller_afdeling_id: bellerAfdelingId || null,
        beller_actief:      bellerActief,
      });
      toast.success('Standaardwaarden opgeslagen.');
      if (herlaadGebruiker) herlaadGebruiker();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt.');
    } finally {
      setBellerLaden(false);
    }
  }

  if (laden) return <div className={styles.laden}>Laden...</div>;
  if (!profiel) return null;

  return (
    <div className={styles.pagina}>
      <div className={styles.header}>
        <button className={styles.terugKnop} onClick={() => navigate(-1)}>← Terug</button>
        <h1 className={styles.titel}>Mijn profiel</h1>
      </div>

      <div className={styles.grid}>

        {/* ── Persoonlijke gegevens ── */}
        <div className={styles.kaart}>
          <h2 className={styles.kaartTitel}>Persoonlijke gegevens</h2>
          <div className={styles.gegeven}>
            <span className={styles.gegevenLabel}>Naam</span>
            <span className={styles.gegevenWaarde}>{profiel.naam || '—'}</span>
          </div>
          <div className={styles.gegeven}>
            <span className={styles.gegevenLabel}>E-mailadres</span>
            <span className={styles.gegevenWaarde}>{profiel.email}</span>
          </div>
          <div className={styles.gegeven}>
            <span className={styles.gegevenLabel}>Rol</span>
            <span className={styles.gegevenWaarde}>{profiel.rol}</span>
          </div>
          {profiel.locatie_naam && (
            <div className={styles.gegeven}>
              <span className={styles.gegevenLabel}>Locatie</span>
              <span className={styles.gegevenWaarde}>{profiel.locatie_naam}</span>
            </div>
          )}
          {profiel.afdeling_naam && (
            <div className={styles.gegeven}>
              <span className={styles.gegevenLabel}>Afdeling</span>
              <span className={styles.gegevenWaarde}>{profiel.afdeling_naam}</span>
            </div>
          )}
          <p className={styles.infoTekst}>Naam en e-mailadres kunnen alleen worden gewijzigd door een organisatiebeheerder.</p>
        </div>

        {/* ── Wachtwoord wijzigen ── */}
        {heeftPermissie('profiel.wachtwoord_wijzigen') && <div className={styles.kaart}>
          <h2 className={styles.kaartTitel}>Wachtwoord wijzigen</h2>
          <form onSubmit={handleWachtwoord}>
            <div className={styles.veld}>
              <label>Huidig wachtwoord</label>
              <input type="password" value={huidigWw}
                onChange={e => setHuidigWw(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" />
            </div>
            <div className={styles.veld}>
              <label>Nieuw wachtwoord</label>
              <input type="password" value={nieuwWw}
                onChange={e => setNieuwWw(e.target.value)}
                placeholder="Minimaal 8 tekens" autoComplete="new-password" />
            </div>
            <div className={styles.veld}>
              <label>Bevestig nieuw wachtwoord</label>
              <input type="password" value={bevestigWw}
                onChange={e => setBevestigWw(e.target.value)}
                placeholder="••••••••" autoComplete="new-password" />
            </div>
            <button type="submit" className={styles.knopPrimair} disabled={wwLaden}>
              {wwLaden ? 'Opslaan...' : 'Wachtwoord wijzigen'}
            </button>
          </form>
        </div>}

        {/* ── Standaardwaarden beller ── */}
        <div className={styles.kaart}>
          <h2 className={styles.kaartTitel}>Standaardwaarden beoordeling</h2>
          <p className={styles.infoTekst}>
            Vul hieronder uw standaardgegevens in. Deze worden automatisch ingevuld bij het starten van een nieuwe beoordeling als de schakelaar actief is.
          </p>
          <form onSubmit={handleBeller}>
            <div className={styles.schakelaarRij}>
              <label className={styles.schakelaarLabel}>
                <div className={`${styles.schakelaar} ${bellerActief ? styles.aan : ''}`}
                  onClick={() => setBellerActief(!bellerActief)}>
                  <div className={styles.schakelaarKnop} />
                </div>
                Standaardwaarden automatisch invullen
              </label>
            </div>

            <div className={styles.veld}>
              <label>Naam beller</label>
              <input type="text" value={bellerNaam}
                onChange={e => setBellerNaam(e.target.value)}
                placeholder="Uw naam" disabled={!bellerActief} />
            </div>
            <div className={styles.veld}>
              <label>Functie</label>
              <select value={bellerFunctie}
                onChange={e => setBellerFunctie(e.target.value)}
                disabled={!bellerActief}>
                <option value="">Kies uw functie...</option>
                {functies.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            {afdelingen.length > 0 && (
              <div className={styles.veld}>
                <label>Afdeling</label>
                <select value={bellerAfdelingId}
                  onChange={e => setBellerAfdId(e.target.value)}
                  disabled={!bellerActief}>
                  <option value="">Kies uw afdeling...</option>
                  {afdelingen.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.locatie_naam} — {a.naam}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button type="submit" className={styles.knopPrimair} disabled={bellerLaden}>
              {bellerLaden ? 'Opslaan...' : 'Standaardwaarden opslaan'}
            </button>
          </form>
        </div>

        {/* ── Beveiliging ── */}
        <div className={styles.kaart}>
          <h2 className={styles.kaartTitel}>Beveiliging</h2>
          <div className={styles.gegeven}>
            <span className={styles.gegevenLabel}>Tweestapsverificatie</span>
            <span className={`${styles.badge} ${profiel.twofa_actief ? styles.badgeGroen : styles.badgeOranje}`}>
              {profiel.twofa_actief ? '✓ Actief' : '⚠ Niet ingesteld'}
            </span>
          </div>
          {!profiel.twofa_actief && (
            <button className={styles.knopSecundair} onClick={() => navigate('/2fa-setup')}>
              2FA instellen
            </button>
          )}
          {profiel.twofa_actief && (
            <p className={styles.infoTekst}>
              Wilt u uw authenticator-app opnieuw instellen? Neem dan contact op met uw organisatiebeheerder.
            </p>
          )}
        </div>

      </div>
    </div>
  );
}
